import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Loader2, CreditCard, Lock, Check, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

/**
 * CheckoutModal — handles BOTH subscribe and recharge flows. Caller passes:
 *   - mode: "subscribe" | "recharge"
 *   - tier: { id, name, price_cents } OR
 *   - pkg:  { id, tokens, price_cents }
 *   - stripeEnabled: boolean — true → real Stripe redirect flow;
 *                              false → legacy mock flow (Fase 8).
 *   - onSuccess(payload): called with the backend response on 200 (mock path only)
 */
export default function CheckoutModal({ open, mode, tier, pkg, stripeEnabled, onClose, onSuccess }) {
  const { t } = useTranslation();
  const downOnBackdropRef = useRef(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null); // { code, type, value, description }
  const [couponError, setCouponError] = useState(null);
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const basePriceCents = mode === "subscribe" ? tier?.price_cents : pkg?.price_cents;

  // Reset state on open/close.
  useEffect(() => {
    if (!open) return undefined;
    setCouponInput("");
    setCoupon(null);
    setCouponError(null);
    setSubmitting(false);
    setError(null);
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleValidateCoupon = useCallback(async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setValidating(true);
    setCouponError(null);
    try {
      const res = await api.post("/billing/validate-coupon", {
        code,
        context: mode === "subscribe" ? "subscribe" : "recharge",
        tier: mode === "subscribe" ? tier?.id : undefined,
      });
      if (res.data.valid) {
        setCoupon(res.data.coupon);
        setCouponError(null);
      } else {
        setCoupon(null);
        setCouponError(t("billing.coupon.invalid"));
      }
    } catch (e) {
      setCoupon(null);
      setCouponError(t("billing.coupon.invalid"));
    } finally {
      setValidating(false);
    }
  }, [couponInput, mode, tier, t]);

  // Compute effective price + bonus tokens for display.
  const discountCents =
    coupon?.type === "percent_off"
      ? Math.round((basePriceCents * coupon.value) / 100)
      : coupon?.type === "flat_off"
        ? Math.min(basePriceCents, coupon.value)
        : 0;
  const bonusTokens = coupon?.type === "bonus_tokens" ? coupon.value : 0;
  const effectivePrice = Math.max(0, (basePriceCents || 0) - discountCents);

  const handleConfirm = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // Phase 11: when Stripe is enabled, hit the Checkout Session endpoint
      // and redirect to the hosted Stripe page. The backend does NOT complete
      // the fulfilment until the webhook fires — so `onSuccess` is only used
      // in the mock path.
      if (stripeEnabled) {
        const url = mode === "subscribe"
          ? "/billing/checkout/subscribe"
          : "/billing/checkout/recharge";
        const payload = mode === "subscribe"
          ? { tier: tier.id, coupon: coupon?.code, origin_url: window.location.origin }
          : { package_id: pkg.id, coupon: coupon?.code, origin_url: window.location.origin };
        const res = await api.post(url, payload);
        const checkoutUrl = res?.data?.checkout_url;
        if (!checkoutUrl) {
          throw new Error("no_checkout_url");
        }
        // Full-page redirect to Stripe hosted checkout. The user will come
        // back to /billing?success=true&session_id=… once payment completes.
        window.location.href = checkoutUrl;
        return;  // keep the modal in the "submitting" state until the redirect fires
      }

      const payload = mode === "subscribe"
        ? { tier: tier.id, coupon: coupon?.code }
        : { package_id: pkg.id, coupon: coupon?.code };
      const url = mode === "subscribe" ? "/billing/subscribe" : "/billing/recharge";
      const res = await api.post(url, payload);
      onSuccess(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        (typeof detail === "object" && detail?.message) ||
        (typeof detail === "string" && detail) ||
        t("billing.checkout.failed");
      setError(msg);
      setSubmitting(false);
    }
  }, [submitting, mode, tier, pkg, coupon, stripeEnabled, onSuccess, t]);

  if (!open) return null;
  const titleKey = mode === "subscribe" ? "billing.checkout.title_subscribe" : "billing.checkout.title_recharge";
  const titleArgs = mode === "subscribe"
    ? { tier: tier?.name }
    : { tokens: pkg?.tokens };
  const dollars = (effectivePrice / 100).toFixed(2).replace(/\.00$/, "");
  const baseDollars = ((basePriceCents || 0) / 100).toFixed(2).replace(/\.00$/, "");

  return (
    <div
      data-testid="checkout-overlay"
      className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/65 backdrop-blur-sm animate-fade-in-up px-4 py-6 overflow-y-auto"
      role="dialog"
      onMouseDown={(e) => {
        downOnBackdropRef.current = e.target === e.currentTarget;
      }}
      onMouseUp={(e) => {
        const ok = downOnBackdropRef.current && e.target === e.currentTarget && !submitting;
        downOnBackdropRef.current = false;
        if (ok) onClose();
      }}
    >
      <div
        data-testid="checkout-modal"
        className="relative w-full max-w-[400px] rounded-[24px] px-5 sm:px-6 pt-6 pb-5 bg-[rgba(10,14,26,0.96)] backdrop-blur-xl border border-[rgba(0,229,255,0.3)] shadow-[0_30px_80px_-10px_rgba(0,0,0,0.75),0_0_60px_-12px_rgba(0,229,255,0.4)]"
      >
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          aria-label={t("common.close")}
          data-testid="checkout-close"
          className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-white/[0.06] hover:text-[#E5E7EB] disabled:opacity-40"
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>

        <h2
          className="text-[18px] font-semibold text-[#E5E7EB] pr-8"
          data-testid="checkout-title"
        >
          {t(titleKey, titleArgs)}
        </h2>

        {/* Price summary */}
        <div className="mt-4 p-3 rounded-xl bg-white/[0.025] border border-white/[0.06]">
          <div className="flex items-center justify-between text-[12.5px] text-[#9CA3AF]">
            <span>{t("billing.checkout.total")}</span>
            <span className="text-[#E5E7EB]">${baseDollars}</span>
          </div>
          {discountCents > 0 ? (
            <div className="flex items-center justify-between text-[12.5px] text-[#00E5FF] mt-1">
              <span>{t("billing.checkout.discount")}</span>
              <span data-testid="checkout-discount-line">
                -${(discountCents / 100).toFixed(2).replace(/\.00$/, "")}
              </span>
            </div>
          ) : null}
          {bonusTokens > 0 ? (
            <div className="flex items-center justify-between text-[12.5px] text-[#00E5FF] mt-1">
              <span>{t("billing.checkout.bonus_tokens")}</span>
              <span data-testid="checkout-bonus-line">+{bonusTokens}</span>
            </div>
          ) : null}
          <div className="mt-2 pt-2 border-t border-white/[0.05] flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-[0.18em] text-[#9CA3AF]">Total</span>
            <span
              data-testid="checkout-final-price"
              className="text-[20px] font-mono font-semibold text-[#E5E7EB]"
            >
              ${dollars}
            </span>
          </div>
        </div>

        {/* Coupon input */}
        <div className="mt-4">
          <label className="block text-[10.5px] uppercase tracking-[0.18em] text-[#9CA3AF] mb-1.5">
            {t("billing.coupon.label")}
          </label>
          <div className="flex items-stretch gap-2">
            <input
              type="text"
              value={couponInput}
              onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
              placeholder={t("billing.coupon.placeholder")}
              data-testid="checkout-coupon-input"
              className="flex-1 h-9 px-3 rounded-xl bg-white/[0.03] border border-white/[0.08] text-[12.5px] text-[#E5E7EB] placeholder:text-[#6B7280] focus:outline-none focus:border-[rgba(0,229,255,0.4)]"
              disabled={submitting || !!coupon}
            />
            {coupon ? (
              <button
                type="button"
                onClick={() => {
                  setCoupon(null);
                  setCouponInput("");
                }}
                data-testid="checkout-coupon-remove"
                className="h-9 px-3 rounded-full text-[11.5px] border border-white/[0.1] text-[#E5E7EB] hover:bg-white/[0.06]"
              >
                {t("billing.coupon.remove")}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleValidateCoupon}
                disabled={!couponInput.trim() || validating || submitting}
                data-testid="checkout-coupon-apply"
                className="h-9 px-3 rounded-full text-[11.5px] font-semibold bg-white/[0.04] border border-white/[0.1] text-[#E5E7EB] hover:bg-white/[0.08] disabled:opacity-40 inline-flex items-center gap-1"
              >
                {validating ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} /> : null}
                {t("billing.coupon.apply")}
              </button>
            )}
          </div>
          {coupon ? (
            <p
              data-testid="checkout-coupon-applied"
              className="mt-1.5 text-[11.5px] text-[#00E5FF] inline-flex items-center gap-1"
            >
              <Check className="h-3 w-3" strokeWidth={2.5} />
              {t("billing.coupon.applied")}: <span className="font-mono">{coupon.code}</span>
            </p>
          ) : null}
          {couponError ? (
            <p
              data-testid="checkout-coupon-error"
              className="mt-1.5 text-[11.5px] text-[#F87171]"
            >
              {couponError}
            </p>
          ) : null}
        </div>

        {/* Mock card (only shown in mock mode) */}
        {!stripeEnabled ? (
          <div className="mt-4 p-3 rounded-xl bg-[#03060C] border border-white/[0.08] font-mono text-[12px] text-[#E5E7EB]/85">
            <div className="flex items-center justify-between text-[#9CA3AF] text-[10px] uppercase tracking-[0.18em] mb-2">
              <span>{t("billing.checkout.card_number")}</span>
              <CreditCard className="h-3.5 w-3.5" strokeWidth={1.75} />
            </div>
            <p className="tracking-widest">•••• •••• •••• 4242</p>
            <div className="mt-2 flex items-center justify-between text-[11px] text-[#9CA3AF]">
              <span>12/34 · CVC •••</span>
              <span className="text-[10px] uppercase tracking-[0.18em]">{t("billing.checkout.card_note")}</span>
            </div>
          </div>
        ) : (
          <div
            data-testid="checkout-stripe-connected"
            className="mt-4 p-3 rounded-xl bg-[rgba(0,229,255,0.04)] border border-[rgba(0,229,255,0.25)] flex items-center gap-2.5"
          >
            <div className="h-8 w-8 rounded-full bg-[rgba(0,229,255,0.15)] border border-[rgba(0,229,255,0.5)] flex items-center justify-center shrink-0">
              <Check className="h-4 w-4 text-[#00E5FF]" strokeWidth={2.5} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-semibold text-[#E5E7EB]">
                {t("billing.checkout.stripe_connected_title")}
              </p>
              <p className="text-[10.5px] text-[#9CA3AF] leading-snug">
                {t("billing.checkout.stripe_connected_hint")}
              </p>
            </div>
          </div>
        )}

        {!stripeEnabled ? (
          <button
            type="button"
            disabled
            data-testid="checkout-stripe-soon"
            className="mt-3 w-full h-9 rounded-full text-[11.5px] border border-white/[0.08] text-[#6B7280] bg-white/[0.02] inline-flex items-center justify-center gap-1.5 cursor-not-allowed"
            title={t("billing.checkout.stripe_soon")}
          >
            <Lock className="h-3 w-3" strokeWidth={2} />
            {t("billing.checkout.stripe_soon")}
          </button>
        ) : null}

        {error ? (
          <p
            data-testid="checkout-error"
            className="mt-3 px-3 py-2 rounded-xl text-[11.5px] text-[#F87171] bg-[rgba(239,68,68,0.06)] border border-[rgba(239,68,68,0.3)]"
          >
            {error}
          </p>
        ) : null}

        <button
          type="button"
          onClick={handleConfirm}
          disabled={submitting}
          data-testid="checkout-confirm"
          className="mt-4 w-full h-11 rounded-full text-[13px] font-semibold bg-[rgba(0,229,255,0.18)] border border-[rgba(0,229,255,0.55)] text-[#00E5FF] hover:bg-[rgba(0,229,255,0.28)] shadow-[0_0_22px_rgba(0,229,255,0.35)] disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
        >
          {submitting ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
              {stripeEnabled
                ? t("billing.checkout.redirecting")
                : t("billing.checkout.processing")}
            </>
          ) : (
            <>
              {stripeEnabled ? (
                <ExternalLink className="h-3.5 w-3.5" strokeWidth={2} />
              ) : null}
              {stripeEnabled
                ? t("billing.checkout.pay_with_stripe")
                : t("billing.checkout.confirm")}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
